import React, { Fragment, useEffect, useId, useRef, useState } from "react";

import { faSearch } from "@fortawesome/pro-regular-svg-icons";
import { Dialog, DialogPanel } from "@headlessui/react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useDebounceCallback } from "usehooks-ts";

import { EmptyState } from "src/design-system/EmptyState";
import { Icon } from "src/design-system/Icon";
import { Omnibox } from "src/omnibox/Omnibox";
import { useFuseSearch } from "src/utils/useFuseSearch";

export type MenuItem = {
  icon?: React.ReactNode;
  label: string | React.ReactNode;
  type: string;
  subType?: string;
  /** Section heading above the first item of this `type`; defaults to the pluralized type. */
  groupLabel?: string;
  value: string;
  details: string;
  provider?: string;
  onSelect?: (query: string) => void;
  pattern?: RegExp;
  disabled?: boolean;
  dataLoc?: string;
  hideSection?: boolean;
  alwaysInclude?: boolean;
};

type OmniboxBaseProps = {
  open: boolean;
  menuItems: MenuItem[];
  onClose: () => void;
  onQuery?: (query: string) => void;
  placeholder: string;
};

const SECTION_HEIGHT = 32;
const ROW_HEIGHT = 40;

const getSectionLabel = (item: MenuItem): string =>
  item.groupLabel ?? `${item.type}s`;

const getItemDataLoc = (item: MenuItem): string | undefined =>
  item.dataLoc
    ? `${item.dataLoc}${item.disabled ? "-disabled" : ""}`
    : undefined;

export const OmniboxBase: React.FC<OmniboxBaseProps> = ({
  open,
  menuItems,
  placeholder,
  onClose,
  onQuery,
}) => {
  const listId = useId();
  const parentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(0);
  const [isVisible, setIsVisible] = useState(open);

  // Debounced function to handle input changes
  const handleInputChange = useDebounceCallback((value: string) => {
    setQuery(value);
    onQuery?.(value);
  }, 150);

  const search = useFuseSearch(menuItems, {
    keys: ["label", "details", "type", "value", "provider"],
    shouldSort: true,
    threshold: 0.5,
  });
  const filteredMenu = search(query);

  const rowVirtualizer = useVirtualizer({
    enabled: isVisible,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      if (index === 0) {
        return filteredMenu[index].hideSection
          ? ROW_HEIGHT
          : ROW_HEIGHT + SECTION_HEIGHT;
      }

      return filteredMenu[index - 1].type !== filteredMenu[index].type
        ? ROW_HEIGHT + SECTION_HEIGHT
        : ROW_HEIGHT;
    },
    count: filteredMenu.length,
    overscan: 5,
    getItemKey: (index) => filteredMenu[index].value,
  });

  // Update focused item index when filtered menu changes
  useEffect(() => {
    setFocused(Math.max(Math.min(focused, filteredMenu.length - 1), 0));
  }, [filteredMenu.length, focused]);

  const getNextIndex = (currentIndex: number, direction: 1 | -1) => {
    for (
      let i = currentIndex + direction;
      i >= 0 && i < filteredMenu.length;
      i += direction
    ) {
      if (!filteredMenu[i].disabled) {
        return i;
      }
    }

    // if no non-disabled element is found return the current index
    return currentIndex;
  };

  // Handle key events
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        setFocused((prev) => getNextIndex(prev, 1));
        break;
      case "ArrowUp":
        setFocused((prev) => getNextIndex(prev, -1));
        break;
      case "Enter": {
        const focusedItem = filteredMenu[focused];
        if (!focusedItem?.disabled) {
          focusedItem?.onSelect?.(query);
          onClose(); // Close the dialog after selection
        }
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    rowVirtualizer.scrollToIndex(focused, {
      align: "auto",
      behavior: "smooth",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused, rowVirtualizer.scrollToIndex]);

  const getItemId = (index: number): string => `${listId}-option-${index}`;
  const hasResults = filteredMenu.length > 0;

  return (
    <Dialog
      className="relative z-50"
      open={open}
      autoFocus
      onClose={onClose}
      onTransitionEnd={() => {
        if (open) {
          inputRef.current?.focus();
          setIsVisible(true);
        } else {
          setQuery("");
          setIsVisible(false);
        }
      }}
    >
      <div className="fixed inset-0 justify-center p-4">
        <DialogPanel
          className="mx-auto mt-48 w-full max-w-lg transition duration-100 ease-out data-[closed]:scale-90 data-[closed]:opacity-0"
          transition
        >
          <Omnibox>
            <div className="flex gap-2 px-4 pt-4">
              <Icon variant="default" size="md" icon={faSearch} />
              <input
                ref={inputRef}
                aria-activedescendant={
                  hasResults ? getItemId(focused) : undefined
                }
                aria-autocomplete="list"
                aria-controls={listId}
                aria-expanded={hasResults}
                className="w-full bg-transparent font-inter-normal-13px text-gray-900 outline-none placeholder:text-gray-400"
                data-loc="omnibox-input"
                placeholder={placeholder}
                role="combobox"
                spellCheck="false"
                onBlur={() => null}
                onChange={(event) => handleInputChange(event.target.value)}
                onKeyDown={handleKeyDown}
              />
            </div>
            <div className="mt-3 h-px w-full bg-gray-200 bg-opacity-50"></div>
            {hasResults ? (
              <Omnibox.List
                ref={parentRef}
                aria-label={placeholder}
                id={listId}
                maxHeight="lg"
              >
                <div
                  className="relative"
                  style={{
                    height: `${rowVirtualizer.getTotalSize() || 376}px`,
                  }}
                >
                  {rowVirtualizer.getVirtualItems().map((virtualItem) => {
                    const item = filteredMenu[virtualItem.index];

                    const showSection =
                      virtualItem.index === 0
                        ? !item.hideSection
                        : filteredMenu[virtualItem.index - 1].type !==
                          item.type;
                    const rowStart = showSection
                      ? virtualItem.start + SECTION_HEIGHT
                      : virtualItem.start;

                    return (
                      <Fragment key={virtualItem.key}>
                        {showSection && (
                          <div
                            className="absolute top-0 left-0 w-full"
                            style={{
                              transform: `translateY(${virtualItem.start}px)`,
                            }}
                          >
                            <Omnibox.GroupHeading>
                              {getSectionLabel(item)}
                            </Omnibox.GroupHeading>
                          </div>
                        )}
                        <div
                          className="absolute top-0 left-0 w-full"
                          style={{
                            transform: `translateY(${rowStart}px)`,
                          }}
                        >
                          <Omnibox.Item
                            dataLoc={getItemDataLoc(item)}
                            icon={item.icon}
                            id={getItemId(virtualItem.index)}
                            isDisabled={item.disabled}
                            isHighlighted={focused === virtualItem.index}
                            label={item.label}
                            meta={item.subType ?? item.type}
                            onClick={(e) => {
                              if (item.onSelect && !item.disabled) {
                                item.onSelect(query);
                                if (!e.metaKey) {
                                  onClose();
                                }
                              }
                            }}
                            onMouseMove={() => setFocused(virtualItem.index)}
                          />
                        </div>
                      </Fragment>
                    );
                  })}
                </div>
              </Omnibox.List>
            ) : (
              <EmptyState
                description="No items match your search or selection criteria."
                headline="No Results Found"
                icon={faSearch}
              />
            )}
          </Omnibox>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
